import React, { useState, useEffect } from 'react';
import "../onboarding/admin/onboardingAdmin.css";
import "./programadmin.css";

// Define API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const ProgramAdmin = () => {
    const [programs, setPrograms] = useState([]);
    const [currentProgram, setCurrentProgram] = useState({
        name: "",
        initialBonus: "0",
        commission: "0",
        rules: {
            benefits: [""],
            requirements: [""],
            note: ""
        }
    });
    const [message, setMessage] = useState({ text: "", type: "" });
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        fetchPrograms();
    }, []);

    const fetchPrograms = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/programs`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            
            // Sort programs by created_at date (newest first)
            const sortedPrograms = [...data.programs].sort((a, b) => {
                return new Date(b.created_at) - new Date(a.created_at);
            });
            
            setPrograms(sortedPrograms);
        } catch (error) {
            console.error("Error fetching programs:", error);
            setMessage({ text: `Error fetching programs: ${error.message}`, type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setCurrentProgram({
            ...currentProgram,
            [name]: value
        });
    };

    const handleRulesChange = (e, type, index) => {
        const { value } = e.target;
        const updatedRules = { ...currentProgram.rules };
        
        if (type === "benefits" || type === "requirements") {
            updatedRules[type][index] = value;
        } else {
            updatedRules[type] = value;
        }
        
        setCurrentProgram({
            ...currentProgram,
            rules: updatedRules
        });
    };

    const addRuleItem = (type) => {
        const updatedRules = { ...currentProgram.rules };
        updatedRules[type] = [...updatedRules[type], ""];
        
        setCurrentProgram({
            ...currentProgram,
            rules: updatedRules
        });
    };

    const removeRuleItem = (type, index) => {
        const updatedRules = { ...currentProgram.rules };
        updatedRules[type] = updatedRules[type].filter((_, i) => i !== index);
        
        setCurrentProgram({
            ...currentProgram,
            rules: updatedRules
        });
    };

    const resetForm = () => {
        setCurrentProgram({
            name: "",
            initialBonus: "0",
            commission: "0",
            rules: {
                benefits: [""],
                requirements: [""],
                note: ""
            }
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Form validation
            if (!currentProgram.name) {
                setMessage({ text: "Program name is required", type: "error" });
                return;
            }

            // Filter out empty benefits and requirements
            const filteredProgram = {
                ...currentProgram,
                rules: {
                    ...currentProgram.rules,
                    benefits: currentProgram.rules.benefits.filter(benefit => benefit.trim() !== ""),
                    requirements: currentProgram.rules.requirements.filter(req => req.trim() !== "")
                }
            };

            const url = isEditing 
                ? `${API_BASE_URL}/api/programs/${editingId}` 
                : `${API_BASE_URL}/api/programs`;
            
            const method = isEditing ? "PUT" : "POST";
            
            const response = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(filteredProgram)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to ${isEditing ? 'update' : 'create'} program`);
            }

            setMessage({ 
                text: `Program ${isEditing ? 'updated' : 'created'} successfully!`, 
                type: "success" 
            });
            
            // Clear the form and refresh programs
            resetForm();
            fetchPrograms();
            
            // Clear message after 3 seconds
            setTimeout(() => setMessage({ text: "", type: "" }), 3000);
            
        } catch (error) {
            console.error(`Error ${isEditing ? 'updating' : 'creating'} program:`, error);
            setMessage({ text: error.message, type: "error" });
        }
    };

    const handleEdit = (program) => {
        // Ensure rules is properly formatted
        const formattedProgram = {
            ...program,
            rules: typeof program.rules === 'string' 
                ? JSON.parse(program.rules) 
                : program.rules
        };
        
        // Make sure rules has the expected structure
        if (!formattedProgram.rules.benefits) formattedProgram.rules.benefits = [""];
        if (!formattedProgram.rules.requirements) formattedProgram.rules.requirements = [""];
        if (!formattedProgram.rules.note) formattedProgram.rules.note = "";
        
        setCurrentProgram(formattedProgram);
        setIsEditing(true);
        setEditingId(program.id);
        
        // Scroll to the form
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id, name) => {
        if (window.confirm(`Are you sure you want to delete the program "${name}"?`)) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/programs/${id}`, {
                    method: "DELETE"
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.error || "Failed to delete program");
                }

                setMessage({ text: `Program "${name}" deleted successfully!`, type: "success" });
                fetchPrograms();
                
                // Clear message after 3 seconds
                setTimeout(() => setMessage({ text: "", type: "" }), 3000);
                
            } catch (error) {
                console.error("Error deleting program:", error);
                setMessage({ text: error.message, type: "error" });
            }
        }
    };

    return (
        <div className="program-admin-container">
            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}
            
            <div className="program-form-section">
                <h3 className="form-section-title">
                    {isEditing ? "Edit Program" : "Create New Program"}
                </h3>
                
                <form className="program-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Program Name</label>
                        <input
                            type="text"
                            name="name"
                            value={currentProgram.name}
                            onChange={handleFormChange}
                            placeholder="Enter program name"
                            className="form-input"
                        />
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label>Initial Bonus (RON)</label>
                            <input
                                type="number"
                                name="initialBonus"
                                value={currentProgram.initialBonus}
                                onChange={handleFormChange}
                                placeholder="0"
                                className="form-input"
                            />
                        </div>
                        
                        <div className="form-group">
                            <label>Commission (%)</label>
                            <input
                                type="number"
                                name="commission"
                                value={currentProgram.commission}
                                onChange={handleFormChange}
                                placeholder="0"
                                className="form-input"
                            />
                        </div>
                    </div>
                    
                    <div className="form-section">
                        <h4>Benefits</h4>
                        {currentProgram.rules.benefits.map((benefit, index) => (
                            <div className="rule-item" key={`benefit-${index}`}>
                                <input
                                    type="text"
                                    value={benefit}
                                    onChange={(e) => handleRulesChange(e, "benefits", index)}
                                    placeholder="Enter a benefit"
                                    className="form-input"
                                />
                                <button 
                                    type="button" 
                                    className="remove-rule-btn"
                                    onClick={() => removeRuleItem("benefits", index)}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        ))}
                        <button 
                            type="button" 
                            className="add-rule-btn"
                            onClick={() => addRuleItem("benefits")}
                        >
                            <i className="fas fa-plus"></i> Add Benefit
                        </button>
                    </div>
                    
                    <div className="form-section">
                        <h4>Requirements</h4>
                        {currentProgram.rules.requirements.map((requirement, index) => (
                            <div className="rule-item" key={`requirement-${index}`}>
                                <input
                                    type="text"
                                    value={requirement}
                                    onChange={(e) => handleRulesChange(e, "requirements", index)}
                                    placeholder="Enter a requirement"
                                    className="form-input"
                                />
                                <button 
                                    type="button" 
                                    className="remove-rule-btn"
                                    onClick={() => removeRuleItem("requirements", index)}
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        ))}
                        <button 
                            type="button" 
                            className="add-rule-btn"
                            onClick={() => addRuleItem("requirements")}
                        >
                            <i className="fas fa-plus"></i> Add Requirement
                        </button>
                    </div>
                    
                    <div className="form-group">
                        <label>Note</label>
                        <textarea
                            name="note"
                            value={currentProgram.rules.note}
                            onChange={(e) => handleRulesChange(e, "note")}
                            placeholder="Enter any additional notes"
                            className="form-textarea"
                            rows={4}
                        />
                    </div>
                    
                    <div className="form-actions">
                        <button type="submit" className="submit-btn">
                            {isEditing ? "Update Program" : "Create Program"}
                        </button>
                        {isEditing && (
                            <button 
                                type="button" 
                                className="cancel-btn"
                                onClick={resetForm}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
            </div>
            
            <div className="program-list-section">
                <h3 className="list-section-title">Existing Programs</h3>
                
                {isLoading ? (
                    <div className="loading-spinner">Loading programs...</div>
                ) : programs.length === 0 ? (
                    <div className="no-programs-message">No programs found. Create your first program above.</div>
                ) : (
                    <div className="program-cards">
                        {programs.map((program) => (
                            <div className="program-card" key={program.id}>
                                <div className="program-card-header">
                                    <h3>{program.name}</h3>
                                    <div className="program-card-actions">
                                        <button 
                                            className="card-action-btn edit-btn"
                                            onClick={() => handleEdit(program)}
                                        >
                                            <i className="fas fa-edit"></i>
                                        </button>
                                        <button 
                                            className="card-action-btn delete-btn"
                                            onClick={() => handleDelete(program.id, program.name)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="program-card-stats">
                                    <div className="stat-item-onboarding">
                                        <span className="stat-label-onboarding">Initial Bonus:</span>
                                        <span className="stat-value-onboarding">{program.initialBonus} RON</span>
                                    </div>
                                    <div className="stat-item-onboarding">
                                        <span className="stat-label-onboarding">Commission:</span>
                                        <span className="stat-value-onboarding">{program.commission}%</span>
                                    </div>
                                    <div className="stat-item-onboarding">
                                        <span className="stat-label-onboarding">Created:</span>
                                        <span className="stat-value-onboarding">
                                            {new Date(program.created_at).toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="program-card-details">
                                    {program.rules && (
                                        <>
                                            {typeof program.rules === 'string' ? (
                                                // Parse rules if they're stored as a string
                                                (() => {
                                                    try {
                                                        const parsedRules = JSON.parse(program.rules);
                                                        return (
                                                            <>
                                                                {parsedRules.benefits && parsedRules.benefits.length > 0 && (
                                                                    <div className="rule-section">
                                                                        <h4>Benefits</h4>
                                                                        <ul>
                                                                            {parsedRules.benefits.map((benefit, index) => (
                                                                                <li key={index}>{benefit}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                                
                                                                {parsedRules.requirements && parsedRules.requirements.length > 0 && (
                                                                    <div className="rule-section">
                                                                        <h4>Requirements</h4>
                                                                        <ul>
                                                                            {parsedRules.requirements.map((req, index) => (
                                                                                <li key={index}>{req}</li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                )}
                                                                
                                                                {parsedRules.note && (
                                                                    <div className="note-section">
                                                                        <p><em>{parsedRules.note}</em></p>
                                                                    </div>
                                                                )}
                                                            </>
                                                        );
                                                    } catch (e) {
                                                        return <p>Invalid rules format</p>;
                                                    }
                                                })()
                                            ) : (
                                                // Rules are already an object
                                                <>
                                                    {program.rules.benefits && program.rules.benefits.length > 0 && (
                                                        <div className="rule-section">
                                                            <h4>Benefits</h4>
                                                            <ul>
                                                                {program.rules.benefits.map((benefit, index) => (
                                                                    <li key={index}>{benefit}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    
                                                    {program.rules.requirements && program.rules.requirements.length > 0 && (
                                                        <div className="rule-section">
                                                            <h4>Requirements</h4>
                                                            <ul>
                                                                {program.rules.requirements.map((req, index) => (
                                                                    <li key={index}>{req}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    
                                                    {program.rules.note && (
                                                        <div className="note-section">
                                                            <p><em>{program.rules.note}</em></p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProgramAdmin;